import { Company } from '~/types/company';
import { Counterpart, CounterpartDirection } from '~/types/counterparts';
import { Document } from '~/types/document';
import { FinancialStatementItemReference } from '~/types/financial-statement';
import { Flow, FlowWithDocument } from '~/types/flow';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Money } from '~/types/money';
import { Transaction, TransactionsFiltersFields, TransactionSorting } from '~/types/transaction';
import { VATBook, VATEntrySource, VATRegistration, VATReportingPeriod } from '~/types/vat-bookkeeping';
import { SortInfo } from '~/utils/jsonapi-query';

export type BookkeepingTransactionSorting = 'date';

export const BookkeepingAccountQueryKeys = {
  all: ['BookkeepingAccounts'],
  byCompany: (companyId: string) => ['BookkeepingAccounts', companyId],
  list: (companyId: string, filters: BookkeepingAccountFilters) => ['BookkeepingAccounts', companyId, 'list', filters],
  listWithFinancialStatementItem: (companyId: string, filters?: BookkeepingAccountFilters) => [
    'BookkeepingAccounts',
    companyId,
    'listWithFinancialStatementItem',
    ...(filters !== undefined ? [filters] : []),
  ],
  infiniteList: (companyId: string, filters: BookkeepingAccountFilters) => [
    'BookkeepingAccounts',
    companyId,
    'infiniteList',
    filters,
  ],
  item: (id: BookkeepingAccount['id']) => ['BookkeepingAccounts', { id }],
  byCodes: (companyId: string, codes: (string | null)[]) => ['BookkeepingAccounts', companyId, 'byCodes', codes],
};

export const BookkeepingBankTransactionsKeys = {
  all: ['BookkeepingBankTransactions'],
  list: (companyId: string, filters: TransactionsFiltersFields, sorting: SortInfo<TransactionSorting>) => [
    'BookkeepingBankTransactions',
    companyId,
    filters,
    sorting,
  ],
  allItems: ['BookkeepingBankTransaction'],
  item: (id: BookkeepingBankTransaction['id']) => ['BookkeepingBankTransaction', id],
  registrationProposal: (transactionId: Transaction['id']) => [
    'BookkeepingBankTransaction',
    transactionId,
    'registrationProposal',
  ],
};

export const BookkeepingTransactionQueryKeys = {
  all: ['BookkeepingTransactions'],
  list: (
    companyId: string,
    filters: BookkeepingTransactionFilters,
    sorting?: SortInfo<BookkeepingTransactionSorting>,
  ) => ['BookkeepingTransactions', companyId, 'list', filters, sorting],
  listForSection: (
    companyId: string,
    filters: BookkeepingTransactionFilters,
    section: LedgerCardsSection,
    sorting?: SortInfo<BookkeepingTransactionSorting>,
  ) => ['BookkeepingTransactions', companyId, 'list', filters, section, sorting],
  metadata: (companyId: string, filters: BookkeepingTransactionFilters, section?: LedgerCardsSection) => [
    'BookkeepingTransactionsMetadata',
    companyId,
    'metadata',
    filters,
    section,
  ],
  item: (id: BookkeepingTransaction['id']) => ['BookkeepingTransactions', { id }],
  downloadBookkeepingTransactions: (filters: DownloadBookkeepingFilters) =>
    ['DownloadBookkeepingTransactions', { filters }] as const,
  sharedBookkeepingTransactions: (token: string) => ['SharedBookkeepingTransactions', { token }] as const,
};

export enum BookkeepingAccountType {
  SP = 'SP', // Stato Patrimoniale (Balance Sheet)
  CE = 'CE', // Conto Economico (Income Statement)
}

export enum BookkeepingAccountSection {
  Attivo = 'ATTIVO', // Assets
  Passivo = 'PASSIVO', // Liabilities
  Ricavi = 'RICAVI', // Revenues
  Costi = 'COSTI', // Costs
}

export enum BookkeepingAdjustmentType {
  RISCONTO_ATTIVO = 'RISCONTO_ATTIVO',
  RISCONTO_PASSIVO = 'RISCONTO_PASSIVO',
  RATEO_ATTIVO = 'RATEO_ATTIVO',
  RATEO_PASSIVO = 'RATEO_PASSIVO',
  FATTURA_DA_EMETTERE = 'FATTURA_DA_EMETTERE',
  FATTURA_DA_RICEVERE = 'FATTURA_DA_RICEVERE',
  NOTA_CREDITO_DA_EMETTERE = 'NOTA_CREDITO_DA_EMETTERE',
  NOTA_CREDITO_DA_RICEVERE = 'NOTA_CREDITO_DA_RICEVERE',
  AMMORTAMENTO = 'AMMORTAMENTO',
}

/**
 * Extended type used exclusively when building API filters.
 * `'NONE'` is a phantom value that matches transactions with no `adjustment_id`
 * (plain and source transactions).
 */
export type BookkeepingAdjustmentTypeFilter = BookkeepingAdjustmentType | 'NONE';

export enum BookkeepingAdjustmentGroup {
  Ratei = 'RATEI',
  Risconti = 'RISCONTI',
  Ammortamenti = 'AMMORTAMENTI',
  FattureDaEmettereRicevere = 'FATTURE_DA_EMETTERE_RICEVERE',
}

export const bookkeepingAdjustmentGroupToTypes: Record<BookkeepingAdjustmentGroup, BookkeepingAdjustmentType[]> = {
  [BookkeepingAdjustmentGroup.Ratei]: [BookkeepingAdjustmentType.RATEO_ATTIVO, BookkeepingAdjustmentType.RATEO_PASSIVO],
  [BookkeepingAdjustmentGroup.Risconti]: [
    BookkeepingAdjustmentType.RISCONTO_ATTIVO,
    BookkeepingAdjustmentType.RISCONTO_PASSIVO,
  ],
  [BookkeepingAdjustmentGroup.Ammortamenti]: [],
  [BookkeepingAdjustmentGroup.FattureDaEmettereRicevere]: [
    BookkeepingAdjustmentType.FATTURA_DA_EMETTERE,
    BookkeepingAdjustmentType.FATTURA_DA_RICEVERE,
    BookkeepingAdjustmentType.NOTA_CREDITO_DA_EMETTERE,
    BookkeepingAdjustmentType.NOTA_CREDITO_DA_RICEVERE,
  ],
};

export enum BookkeepingAdjustmentSourceType {
  BOOKKEEPING_ENTRY = 'BOOKKEEPING_ENTRY',
  VAT_ENTRY = 'VAT_ENTRY',
}

export interface BookkeepingAdjustment {
  id: string;
  adjustmentType: BookkeepingAdjustmentType;
  sourceType: BookkeepingAdjustmentSourceType;
  bookkeepingAccount: BookkeepingAccount;
}

export interface BookkeepingAccount {
  id: string;
  code: string;
  name: string;
  accountType: BookkeepingAccountType;
  section: BookkeepingAccountSection;
  parent?: BookkeepingAccount;
  financialStatementItem?: FinancialStatementItemReference;
}

export interface BookkeepingBankTransaction extends Transaction {
  recommendedReconciliationFlow?: FlowWithDocument;
  registrationProposal?: TransactionRegistrationProposal;
}

export interface BookkeepingTransaction {
  id: string;
  company: Company;
  number?: string;
  date: string;
  updatedAt: string;
  description: string;
  bookkeepingEntries: BookkeepingEntry[];
  vatRegistration?: VATRegistration;
  transactions: Transaction[];
  vatSettlementPeriod?: VATReportingPeriod;
  adjustmentId?: string | null;
}

export interface BookkeepingEntry {
  id: string;
  bookkeepingTransaction: BookkeepingTransaction;
  bookkeepingAccount: BookkeepingAccount;
  amount: Money;
  description?: string;
  counterpart?: Counterpart | null;
  flow?: Flow | null;
  document?: Document | null;
  source: BookkeepingEntrySource;
  competencePeriodStart?: string | null;
  competencePeriodEnd?: string | null;
  bookkeepingAdjustment?: BookkeepingAdjustment | null;
}

export interface BookkeepingLedgerTransaction extends BookkeepingTransaction {
  balances: Money[];
}

export interface ShareBookkeepingLedgerPayload {
  companyId: string;
  fromDate: string | null; // yyyy-MM-dd format
  toDate: string | null; // yyyy-MM-dd format
  bookkeepingAccountId: string;
  counterpartId: string | null;
  subject: string;
  body: string;
  recipients: string | string[]; // Backend accepts both formats
  replyTo?: string; // Optional, defaults to current user email
}

interface SharedBookkeepingEntry {
  registrationDate: string; // ISO date string
  documentDate: string; // ISO date string
  amount: Money;
  balance: Money;
  description: string;
  bookkeepingTransactionId: string;
}

interface SharedBookkeepingAccount {
  code: BookkeepingAccount['code'];
  name: BookkeepingAccount['name'];
  section: BookkeepingAccount['section'];
  type: BookkeepingAccount['accountType'];
}

export interface SharedBookkeepingLedgerResponse {
  data: {
    bookkeepingEntries: SharedBookkeepingEntry[];
    bookkeepingAccount: SharedBookkeepingAccount;
    counterpart: {
      companyName: Counterpart['companyName'];
      taxNumber: Counterpart['taxNumber'];
      vatNumber: Counterpart['vatNumber'];
      type: CounterpartDirection | null;
    } | null;
    company: {
      name: string;
    };
    fromDate: string; // ISO date string
    toDate: string; // ISO date string
  };
}

export interface DownloadBookkeepingFilters {
  company_id: string;
  from_date: string;
  to_date: string;
  bookkeeping_account_id: BookkeepingAccount['id'];
  counterpart_id?: Counterpart['id'];
}

export interface CreateBookkeepingTransaction {
  companyId: Company['id'];
  date: Date;
  description: string;
  bookkeepingEntries: Omit<CreateBookkeepingEntry, 'lid'>[];
  transactionIds: Transaction['id'][];
  recommendedReconciliationFlowId?: Flow['id'] | null;
}

export interface UpdateBookkeepingTransactionForVAT {
  id: string;
  description?: string;
  bookkeepingEntries: Omit<CreateBookkeepingEntry, 'lid'>[];
}

export interface UpdateBookkeepingTransaction extends Omit<
  CreateBookkeepingTransaction,
  'companyId' | 'transactionIds' | 'recommendedReconciliationFlowId'
> {}

export interface CreateBookkeepingAdjustmentPayload {
  adjustmentType: BookkeepingAdjustmentType;
  sourceType: BookkeepingAdjustmentSourceType;
  bookkeepingAccountId: BookkeepingAccount['id'];
  bookkeepingTransactions: Omit<CreateBookkeepingTransaction, 'companyId'>[];
}

export interface CreateBookkeepingEntry {
  lid: string;
  bookkeepingAccountId: BookkeepingAccount['id'];
  counterpartId?: Counterpart['id'] | null;
  documentId?: Document['id'] | null;
  flowId?: Flow['id'] | null;
  amount: Money;
  description?: string;
  source: BookkeepingEntrySource;
  competencePeriodStart?: Date | null;
  competencePeriodEnd?: Date | null;
  bookkeepingAdjustment?: CreateBookkeepingAdjustmentPayload | null;
}
export interface BookkeepingAccountFilters {
  search?: string;
  code?: string;
  codes?: (string | null)[];
  name?: string;
  type?: BookkeepingAccountType;
  section?: BookkeepingAccountSection;
  hasChildren?: boolean;
  used?: boolean;
  hasFinancialStatementItem?: boolean;
}

export interface CreateBookkeepingAccount {
  companyId: Company['id'];
  parentId: BookkeepingAccount['id'];
  code: string;
  name: string;
}

export interface UpdateBookkeepingAccount {
  id: BookkeepingAccount['id'];
  code?: string;
  name: string;
  financialStatementItemId?: string | null;
}

export interface BookkeepingTransactionFilters {
  search?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  description?: string;
  accountId?: BookkeepingAccount['id'];
  counterpartId?: Counterpart['id'];
  transactionDescription?: Transaction['description'];
  amountRange?: [number | null, number | null];
  cashBookAmountRange?: [number | null, number | null];
  adjustmentType?: BookkeepingAdjustmentTypeFilter[];
}

export interface BookkeepingAccountsResponse {
  accounts: BookkeepingAccount[];
  page?: MetaDataCursorPage;
}

export interface BookkeepingTransactionsResponse {
  transactions: BookkeepingTransaction[];
  page?: MetaDataCursorPage;
}

export interface BillsGroup {
  deviceId?: string | null;
  deviceNumber?: string | null;
  month: number; // 1-12
  year: number;
  amount: Money;
}

export interface BillsToRegisterResponse {
  data: BillsGroup[];
}

interface IdSchema {
  id: string; // uuid
}

interface VATRegistrationBulkRegisterVATEntry {
  bookkeepingAccount: IdSchema;
  vatCode: IdSchema;
  taxableAmount: Money;
  taxAmount: Money;
  source: VATEntrySource;
}

interface VATRegistrationBulkRegisterBookkeepingTransaction {
  date: string; // ISO date string
  description: string;
  bookkeepingEntries: VATRegistrationBulkRegisterBookkeepingEntry[];
}

interface VATRegistrationBulkRegisterBookkeepingEntry {
  bookkeepingAccount: IdSchema;
  amount: Money;
}

export interface VATRegistrationBillsVATCodePayload {
  vatCode: IdSchema;
  bookkeepingAccount: IdSchema;
}

export interface VATRegistrationBillsPayload {
  cashBookkeepingAccount: IdSchema | null;
  electronicCashBookkeepingAccount: IdSchema | null;
  ticketCashBookkeepingAccount: IdSchema | null;
  ticketRevenueBookkeepingAccount: IdSchema | null;
  unredeemedGiftBookkeepingAccount: IdSchema | null;
  vatCodes: VATRegistrationBillsVATCodePayload[];
}

export interface VATRegistrationBulkRegisterRequest {
  data: {
    date: string; // ISO date string
    company: IdSchema;
    vatBook: IdSchema;
    vatBookSectional?: IdSchema; // optional because not listed in required[]
    vatReportingPeriod: IdSchema;
    documents: IdSchema[]; // minItems: 1, uniqueItems: true
    vatEntries: VATRegistrationBulkRegisterVATEntry[]; // minItems: 1
    bookkeepingTransaction: VATRegistrationBulkRegisterBookkeepingTransaction;
    vatRegistrationBills?: VATRegistrationBillsPayload;
  }[];
}

export interface VATRegistrationBulkRegisterResponse {
  num: number; // minimum: 1
}

export interface BulkRegisterBookkeepingTransactionsPayload {
  companyId: string;
  attributes: {
    description: string;
    debitAccountId: string;
    creditAccountId: string;
    debitDescription?: string;
    creditDescription?: string;
  };
}
export interface BulkRegisterBookkeepingTransactionsResponse {
  num: number;
}

export interface BulkAcceptBookkeepingTransactionsResponse {
  num: number;
}

export enum BookkeepingEntrySource {
  USER = 'USER',
  SETTINGS = 'SETTINGS',
  RECONCILIATION = 'RECONCILIATION',
  HISTORY = 'HISTORY',
  AUTOMATIC = 'AUTOMATIC',
}

export interface TransactionRegistrationProposalEntry {
  bookkeepingAccount: BookkeepingAccount | null;
  amount: Money;
  counterpart?: Counterpart;
  document?: Document;
  flow?: Flow;
  source: BookkeepingEntrySource;
}

export interface TransactionRegistrationProposal {
  description: string;
  date: Date;
  entries: TransactionRegistrationProposalEntry[];
  transactions: Transaction[];
  recommendedFlowId?: Flow['id'] | null;
}

export interface PreviewVATRegistrationNumberRequest {
  companyId: Company['id'];
  vatBookId: VATBook['id'] | null;
  registrationDate: string;
  vatRegistrationId: VATRegistration['id'] | null;
}

export interface PreviewVATRegistrationNumberResponse {
  number: number;
}

export interface CounterpartBalanceEntry {
  id: string;
  label: string;
  amount: Money;
  direction: CounterpartDirection;
}

// Provisional Balance Types
export const ProvisionalBalanceQueryKeys = {
  bySection: (
    companyId: string,
    section: BookkeepingAccountSection,
    dateFrom: string | null,
    dateTo: string | null,
    adjustmentType: BookkeepingAdjustmentTypeFilter[],
  ) => ['ProvisionalBalance', companyId, section, dateFrom, dateTo, adjustmentType],
  full: (companyId: string, dateFrom: string, dateTo: string) => [
    'ProvisionalBalanceFull',
    companyId,
    dateFrom,
    dateTo,
  ],
  sharedProvisionalBalance: (token: string) => ['SharedProvisionalBalance', { token }],
  sharedProvisionalBalanceAll: ['SharedProvisionalBalance'],
};

export interface ProvisionalBalanceEntry {
  id: string;
  code: string;
  name: string;
  balance: Money;
  hasParent: boolean;
  hasChildren: boolean;
  counterparts?: CounterpartBalanceEntry[];
}

interface ProvisionalBalanceResponse {
  data: ProvisionalBalanceEntry[];
  sectionTotal: Money;
  profitLoss: Money;
}

export interface ShareProvisionalBalancePayload {
  company_id: string;
  from_date: string;
  to_date: string;
  subject: string;
  body: string;
  recipients: string | string[];
  reply_to?: string;
}

export interface SharedProvisionalBalanceData {
  data: {
    attivo: ProvisionalBalanceResponse;
    passivo: ProvisionalBalanceResponse;
    ricavi: ProvisionalBalanceResponse;
    costi: ProvisionalBalanceResponse;
  };
  company: {
    name: string;
  };
  period: {
    from: string;
    to: string;
  };
}

export enum LedgerCardsSection {
  Accounts = 'accounts',
  Clients = 'clients',
  Suppliers = 'suppliers',
}
